import { type ReactNode, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { type Moment } from "moment";
import { twMerge } from "tailwind-merge";

import {
  autoUpdate,
  flip,
  FloatingPortal,
  type Placement,
  type ReferenceType,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { type FixedDateInterval } from "@north-beam/nb-common";
import {
  DateRangeCalendar,
  type DateRangeCalendarValue,
} from "@shared/date-range-calendar";

interface Props {
  value: DateRangeCalendarValue | null;
  maxLookbackDays?: number;
  maxAllowedDate?: Moment;
  placement?: Placement;
  calendarClassName?: string;
  onApply: (value: FixedDateInterval) => void;
  onCancel?: () => void;
  buttonComponent?: (
    ref: (node: ReferenceType | null) => void,
    props: Record<string, unknown>,
  ) => ReactNode;
}

export const DateRangeCalendarSelect = ({
  value,
  placement,
  maxLookbackDays,
  maxAllowedDate,
  calendarClassName,
  onApply,
  onCancel,
  buttonComponent,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: placement || "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [flip(), shift()],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <>
      {buttonComponent ? (
        buttonComponent(refs.setReference, getReferenceProps())
      ) : (
        <button
          className="button sm primary-button font-bold"
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          PICK DATE
        </button>
      )}
      {isOpen && (
        <FloatingPortal>
          <AnimatePresence>
            <motion.div
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              className="z-[2004] nb-date-picker"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <DateRangeCalendar
                className={twMerge(
                  "bg-white rounded-lg elevation-1",
                  calendarClassName,
                )}
                value={value}
                maxLookbackDays={maxLookbackDays}
                onApply={(value) => {
                  onApply(value);
                  setIsOpen(false);
                }}
                onCancel={onCancel}
                maxAllowedDate={maxAllowedDate}
              />
            </motion.div>
          </AnimatePresence>
        </FloatingPortal>
      )}
    </>
  );
};
